import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, tap } from 'rxjs';
import { environment } from '@environments/environment';

// Interface pour typer les données
import { UserData } from '@shared/types/user.type';

@Injectable({
  providedIn: 'root',
})
export class GlobalService {
  apiUrl = environment.api.root;
  private readonly STORAGE_KEY = 'rappelIcr-userData';

  // BehaviorSubject pour stocker et observer les données
  private readonly userDataSubject = new BehaviorSubject<UserData | null>(
    this.loadFromStorage()
  );
  public userData$ = this.userDataSubject.asObservable();

  private readonly http: HttpClient = inject(HttpClient);

  GlobalConnect(params?: { [key: string]: string }): Observable<UserData> {
    const options = { params };
    return this.http
      .get<UserData>(`${this.apiUrl}/authentication/connect`, options)
      .pipe(
        tap((data) => {
          // Stocke les données reçues dans le BehaviorSubject et sessionStorage
          this.userDataSubject.next(data);
          this.saveToStorage(data);
        })
      );
  }

  // Méthode pour récupérer les données de l'utilisateur actuelles
  getCurrentUserData(): UserData | null {
    return this.userDataSubject.getValue();
  }

  // Méthode pour vérifier si les données de l'utilisateur sont chargées
  isDataLoaded(): boolean {
    return this.userDataSubject.getValue() !== null;
  }

  // Méthode pour récuperer les données de l'utilisateur dans le localStorage
  private loadFromStorage(): UserData | null {
    const storedData = sessionStorage.getItem(this.STORAGE_KEY);
    return storedData ? JSON.parse(storedData) : null;
  }

  // Méthode pour enregistrer les données de l'utilisateur dans le localStorage
  private saveToStorage(data: UserData | null): void {
    if (data) {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } else {
      sessionStorage.removeItem(this.STORAGE_KEY);
    }
  }

  // Méthode pour mettre à jour manuellement les données de l'utilisateur
  updateUserData(data: UserData): void {
    this.userDataSubject.next(data);
    this.saveToStorage(data);
  }

  // Méthode pour réinitialiser les données de l'utilisateur
  clearUserData(): void {
    this.userDataSubject.next(null);
    this.saveToStorage(null);
  }
}
